// Le « reste d'hier » et le « ce qui t'attend aujourd'hui » de l'accueil (§6.1).
//
// INFORMATIF UNIQUEMENT. Chaque ligne porte un lien vers l'étape concernée,
// mais aucune action ne se fait depuis l'accueil : on regarde, on entre dans la
// run, on agit là où le travail se fait.

import { fmtEur, formatMedium } from 'sotcp-core/dayUtils'
import { etatCoffre, etatTickets, regles } from './derive'
import type { Consigne, Etape, PadAttente, Rapport } from './types'

export interface Reste {
  cle: string
  titre: string
  detail: string
  gravite: 'warn' | 'stop'
  date: string
  etape?: string
}

/** Seuil V2 de l'écart caisse critique. */
const ECART_CRITIQUE = 5

export function resteDHier(args: {
  datePrec: string | null
  rapportPrec: Rapport | null
  etapesPrec: Etape[]
  pad: PadAttente[]
  consignes: Consigne[]
  aujourdhui: string
}): Reste[] {
  const { datePrec, rapportPrec, etapesPrec, pad, consignes, aujourdhui } = args
  const out: Reste[] = []

  // 1. Étapes mises de côté — la seule des trois non-exécutions qui soit une dette.
  for (const e of etapesPrec.filter((x) => x.statut === 'de_cote')) {
    out.push({
      cle: `park-${e.code}`, titre: `Étape ${e.code} · ${e.libelle} mise de côté`,
      detail: `MOTIF : ${(e.motif ?? 'non précisé').toUpperCase()}`,
      gravite: 'warn', date: e.run_date, etape: e.code,
    })
  }

  // 2. Étapes jamais faites sur une journée qu'on a quittée.
  const jamais = etapesPrec.filter((x) => x.statut === 'a_faire' || x.statut === 'en_cours')
  if (jamais.length > 0 && datePrec) {
    out.push({
      cle: 'non-faites', titre: `${jamais.length} étape${jamais.length > 1 ? 's' : ''} jamais faite${jamais.length > 1 ? 's' : ''}`,
      detail: jamais.map((e) => e.code).join(' · '),
      gravite: 'stop', date: datePrec, etape: jamais[0]?.code,
    })
  }

  // 3. Écarts caisses au-delà du seuil critique V2.
  const ops = Array.isArray(rapportPrec?.rapportOps)
    ? (rapportPrec.rapportOps as Array<Record<string, unknown>>) : []
  for (const o of ops) {
    const diff = Number(o.diff) || 0
    if (Math.abs(diff) >= ECART_CRITIQUE && datePrec) {
      out.push({
        cle: `ecart-${String(o.id ?? o.badge)}`,
        titre: `Écart caisse ${String(o.nom ?? o.badge ?? '')} — ${fmtEur(diff)}`,
        detail: `RAPPORT OPÉRATEUR DU ${formatMedium(datePrec).toUpperCase()}`,
        gravite: 'warn', date: datePrec, etape: '05',
      })
    }
  }

  // 4. Écart de coffre non résolu.
  if (datePrec) {
    const c = etatCoffre(rapportPrec)
    if (c.existe && Math.abs(c.ecart) >= 0.01 && !c.motifEcart.trim()) {
      out.push({
        cle: 'coffre', titre: `Écart de coffre de ${fmtEur(c.ecart)} sans motif`,
        detail: `INVENTAIRE DU ${formatMedium(datePrec).toUpperCase()}`,
        gravite: 'stop', date: datePrec, etape: '10',
      })
    }
  }

  // 5. Tickets jamais vus.
  if (datePrec) {
    const kinds = [
      { k: 'retours' as const, code: '06', nom: 'retours' },
      { k: 'formation' as const, code: '07', nom: 'formation' },
      { k: 'annules' as const, code: '08', nom: 'annulés' },
    ]
    for (const { k, code, nom } of kinds) {
      const e = etatTickets(rapportPrec, k)
      if (e.jamaisVus > 0) {
        out.push({
          cle: `tickets-${k}`, titre: `${e.jamaisVus} ticket${e.jamaisVus > 1 ? 's' : ''} ${nom} jamais contrôlé${e.jamaisVus > 1 ? 's' : ''}`,
          detail: `SUR ${e.total} AU TOTAL`, gravite: 'warn', date: datePrec, etape: code,
        })
      }
    }
  }

  // 6. PAD en attente — ils poursuivent les journées suivantes, par construction.
  for (const p of pad.filter((x) => x.statut === 'en_attente')) {
    out.push({
      cle: `pad-${p.id}`, titre: `PAD ${p.pad_ref} en attente — ${fmtEur(Number(p.montant_attendu))}`,
      detail: `OUVERT LE ${formatMedium(p.journee_origine).toUpperCase()} · SANS JUSTIFICATIF NI TÉLÉCOLLECTE`,
      gravite: 'warn', date: p.journee_origine, etape: '03',
    })
  }

  // 7. Consignes non traitées, y compris celles d'avant-hier.
  for (const c of consignes.filter((x) => x.statut === 'a_faire' && x.journee_cible < aujourdhui)) {
    out.push({
      cle: `consigne-${c.id}`, titre: c.texte,
      detail: `CONSIGNE DE ${(c.auteur_nom ?? '—').toUpperCase()} · ${formatMedium(c.journee_cible).toUpperCase()}`,
      gravite: c.urgence === 3 ? 'stop' : 'warn', date: c.journee_cible,
      ...(c.etape_cible ? { etape: c.etape_cible } : {}),
    })
  }

  return out
}

export interface Attendu { cle: string; texte: string }

/** Les particularités du jour, déduites du calendrier et de l'import. */
export function ceQuiAttend(args: {
  date: string
  rapport: Rapport | null
  journeesOuvertes: string[]
}): Attendu[] {
  const { date, rapport, journeesOuvertes } = args
  const out: Attendu[] = []

  // Cycles de remise V2 : espèces mardi → lundi, chèques lundi → dimanche.
  const jour = new Date(date + 'T12:00:00Z').getUTCDay()
  if (jour === 1) out.push({ cle: 'esp', texte: 'Le cycle de remise espèces (mar → lun) se termine aujourd’hui.' })
  if (jour === 0) out.push({ cle: 'chq', texte: 'Le cycle de remise chèques (lun → dim) se termine aujourd’hui.' })

  const g = regles(rapport)
  const codes = Object.keys(g).sort()
  if (codes.length > 0) {
    out.push({
      cle: 'grisees',
      texte: `${codes.length} étape${codes.length > 1 ? 's' : ''} sera${codes.length > 1 ? 'ont' : ''} grisée${codes.length > 1 ? 's' : ''} d’office : ${codes.join(' · ')}.`,
    })
  } else if (!rapport?.payments) {
    out.push({ cle: 'import', texte: 'Rien n’est encore importé — le parcours entier reste à faire.' })
  }

  const autres = journeesOuvertes.filter((d) => d !== date)
  if (autres.length > 0) {
    out.push({
      cle: 'ouvertes',
      texte: `${autres.length} autre${autres.length > 1 ? 's' : ''} journée${autres.length > 1 ? 's' : ''} encore ouverte${autres.length > 1 ? 's' : ''} : ${autres.join(' · ')}.`,
    })
  }
  return out
}

export interface CoupDoeil { k: string; v: string }

/** Trois ou quatre chiffres d'hier, pour éviter l'aller-retour vers SotCP V3. */
export function coupDoeil(rapport: Rapport | null): CoupDoeil[] {
  const c = etatCoffre(rapport)
  const ops = Array.isArray(rapport?.rapportOps)
    ? (rapport.rapportOps as Array<Record<string, unknown>>) : []
  const declare = ops.reduce((s, o) => s + (Number(o.decl) || 0), 0)
  const ecarts = ops.reduce((s, o) => s + (Number(o.diff) || 0), 0)
  return [
    { k: 'Coffre laissé', v: c.existe ? fmtEur(c.totalCompte) : '—' },
    { k: 'Écart de coffre', v: c.existe ? fmtEur(c.ecart) : '—' },
    { k: 'Déclaré caisses', v: ops.length ? fmtEur(declare) : '—' },
    { k: 'Écarts caisses', v: ops.length ? fmtEur(ecarts) : '—' },
  ]
}
